import type { PresentationMedia } from "@quiz/quiz-definitions";
import { el } from "../utils";

const preloadedImages = new Map<string, HTMLImageElement>();

function imageSource(visual: PresentationMedia, mobile: boolean, reduced: boolean): string {
  return visual.type !== "image" && (reduced || visual.type === "video")
    ? visual.reducedMotionSrc
    : mobile ? visual.mobileSrc ?? visual.src : visual.src;
}

export function preloadCampaignMedia(...visuals: Array<PresentationMedia | undefined>): void {
  const mobile = window.matchMedia("(max-width: 600px)").matches;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  for (const visual of visuals) {
    if (!visual || visual.enabled === false) continue;
    // Warm stills (or video posters) during questions, without fetching video early.
    const sources = new Set([imageSource(visual, mobile, reduced)]);
    if (visual.type === "animated-image") sources.add(visual.reducedMotionSrc);
    for (const src of sources) {
      if (preloadedImages.has(src)) continue;
      const image = new Image();
      image.decoding = "async";
      image.fetchPriority = "low";
      image.src = src;
      preloadedImages.set(src, image);
      void image.decode().catch(() => {
        if (preloadedImages.get(src) === image) preloadedImages.delete(src);
      });
    }
  }
}

export function renderCampaignMedia(visual: PresentationMedia | undefined, className: string): HTMLElement | undefined {
  if (!visual || visual.enabled === false) return undefined;
  const wrap = el("div", `campaign-media ${className}`);
  wrap.dataset.motion = visual.motion ?? "reveal";
  wrap.style.setProperty("--media-fit", visual.crop ?? "contain");
  wrap.style.setProperty("--media-position", visual.objectPosition ?? "center");
  wrap.style.setProperty("--media-mobile-position", visual.mobileObjectPosition ?? visual.objectPosition ?? "center");
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
  const mobile = window.matchMedia("(max-width: 600px)");

  function render(): void {
    wrap.querySelector("video")?.pause();
    wrap.replaceChildren();
    wrap.hidden = false;
    if (!visual) return;
    if (visual.type === "video" && !reduced.matches) {
      const video = document.createElement("video");
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;
      video.width = visual.width;
      video.height = visual.height;
      video.poster = visual.reducedMotionSrc;
      if (visual.alt) video.setAttribute("aria-label", visual.alt);
      else video.setAttribute("aria-hidden", "true");
      const fallback = visual.reducedMotionSrc;
      video.addEventListener("error", () => {
        if (wrap.contains(video)) renderImage(fallback);
      }, { once: true });
      video.src = mobile.matches ? visual.mobileSrc ?? visual.src : visual.src;
      wrap.append(video);
    } else renderImage(imageSource(visual, mobile.matches, reduced.matches));
  }

  function renderImage(src: string): void {
    if (!visual) return;
    const cached = preloadedImages.get(src);
    const image = cached && !cached.isConnected ? cached : document.createElement("img");
    image.alt = visual.alt ?? "";
    image.width = visual.width;
    image.height = visual.height;
    image.decoding = "sync";
    image.fetchPriority = "auto";
    const onError = () => {
      if (!wrap.contains(image)) return;
      if (visual.type === "animated-image" && src !== visual.reducedMotionSrc) renderImage(visual.reducedMotionSrc);
      else wrap.hidden = true;
    };
    image.onerror = onError;
    if (image.getAttribute("src") !== src) image.src = src;
    wrap.replaceChildren(image);
    if (image.complete && image.naturalWidth === 0) onError();
  }

  // Release media-query listeners and stop video when its screen is removed.
  const controller = new AbortController();
  reduced.addEventListener("change", render, { signal: controller.signal });
  mobile.addEventListener("change", render, { signal: controller.signal });
  const observer = new MutationObserver(() => {
    if (!wrap.isConnected) {
      wrap.querySelector("video")?.pause();
      controller.abort();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  render();
  return wrap;
}
